import {
  Component,
  inject,
  ViewChild,
  ElementRef,
  Renderer2,
  NgZone,
  HostListener,
} from '@angular/core';
import { TransferOutService } from '../transfer-out.service';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { InventoryService } from '../../inventory/inventory.service';
import { LocationDto } from '../../inventory/inventory.model';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  Subject,
  switchMap,
  tap,
  Observable,
  firstValueFrom,
} from 'rxjs';
import { PoService } from '../../po/po.service';
import { ToastrService } from 'ngx-toastr';
import { ProductsService } from '../../products/products.service';
import { CanComponentDeactivate } from '../../../../Guard/pending-changes.guard';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import { ReasonsService } from '../reason.service';
import { GrnService } from '../../grn/grn.service';
import { AuthService } from '../../../auth/auth.service';
import {
  ConfigurationService,
  SETTING_KEYS,
} from '../../configuration/configuration.service';
import { QtyInputDirective } from '../../../shared/directives/qty-input.directive';
import { allowsDecimalQty, roundQty } from '../../../shared/utils/quantity.util';
import { FormReloadService } from '../../../core/services/form-reload.service';
import { currentEanOf, withMatchedEan } from '../../../core/utils/current-ean.util';
import { FormDraftStore } from '../../../shared/utils/form-draft-store';

/** The confirm dialog renders its message as HTML. */
function escapeHtml(text: string): string {
  return String(text).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

/** What the browser safety copy of an unsaved Transfer Out holds (see autosaveDraft). */
interface TransferOutDraftSnapshot {
  sourceLocationId: string;
  targetLocationId: string;
  /** The destination row, so the picker can label it before locations load. */
  targetLocation: { id: string; name: string } | null;
  selectedReasonId: string | null;
  remarks: string;
  /** Kept exactly as entered: same SKU on several lines, variantPricingId, soh. */
  transferItems: any[];
}

interface LocationNode {
  id: string;
  name: string;
  type: string;
  childLocations?: LocationNode[];
}
interface TransferOutReason {
  id: string;
  shortReason: string;
  type: string;
  isActive: boolean;
}

@Component({
  selector: 'app-create-transfer-out',
  imports: [FormsModule, CommonModule, NgSelectModule, RouterModule, QtyInputDirective],
  templateUrl: './create-transfer-out.component.html',
  styleUrl: './create-transfer-out.component.scss',
})
export class CreateTransferOutComponent {
  private transferOutService = inject(TransferOutService);
  private inventoryService = inject(InventoryService);
  private poService = inject(PoService);
  private reasonsService = inject(ReasonsService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastr = inject(ToastrService);
  private productService = inject(ProductsService);
  private confirmDialog = inject(ConfirmDialogService);
  private grnService = inject(GrnService);
  private authService = inject(AuthService);
  private configService = inject(ConfigurationService);
  private renderer = inject(Renderer2);
  private sanitizer = inject(DomSanitizer);
  private formReload = inject(FormReloadService);
  private zone = inject(NgZone);
  private isSaved = false;
  private hasUnsavedChanges: boolean = false;

  // Preview Logic
  showPreview = false;
  previewUrl: SafeResourceUrl | null = null;
  private rawBlobUrl: string | null = null;
  isLoadingPreview = false;

  sourceLocationId: string = '';
  // Locations the user may transfer FROM. The source is display-only and
  // follows the store/warehouse switcher at the top (it used to be pinned to
  // the login-time primaryLocationId, which ignored that choice entirely).
  sourceOptions: Array<{ id: string; name: string }> = [];
  targetLocationId: string = '';
  transferOutNumber: string = '';
  transferOutId: string | null = null;
  remarks: string = '';
  reasons: TransferOutReason[] = []; // COMMENT: Property to hold all fetched reasons
  selectedReasonId: string | null = null;
  selectedProduct: any;
  selectedQuantity: number = 0;
  transferItems: any[] = [];
  filteredTargetLocations: any[] = [];
  locations: LocationDto[] = [];
  productSearch$ = new Subject<string>();
  products: any[] = [];
  isSearching = false;
  viewOnly = false;
  selectedVariant: any = null;
  totalAmount: number = 0;

  enteredCostPrice: number = 0;
  allCostPrices: number[] = [];
  allMRPs: number[] = [];
  selectedMRP: number = 0;
  /**
   * Active price generations of the selected variant. The Cost and MRP
   * dropdowns are two views of this one list: picking a cost narrows the
   * MRPs to the generations that carry it, and the pair identifies the
   * pricing row the line will ship — see `selectedPricing`.
   */
  priceOptions: any[] = [];

  grandTotal: number = 0;
  selectedSearchResult: any = null;
  selectedReasonDescription: string = 'N/A';

  // Product-related fields
  vendors: any[] = [];
  vendorSearch$ = new Subject<string>(); // Added for search
  isVendorLoading = false; // Loading indicator
  selectedVendorId: any | null = null; // Track selection

  productCodeSearch$ = new Subject<string>();
  searchResults: any[] = [];
  isCodeSearching = false;
  /** "Search by EAN" box — resolves identity barcodes and price-generation EANs. */
  productEanSearch$ = new Subject<string>();
  eanSearchResults: any[] = [];
  isEanSearching = false;
  selectedSearchType: 'sku' | 'plu' | 'ean' | 'query' = 'sku';
  searchTypes = [
    { value: 'sku', label: 'SKU' },
    { value: 'plu', label: 'PLU' },
    { value: 'ean', label: 'EAN' },
    { value: 'query', label: 'General' },
  ];
  isEdit: boolean = false;

  // Excel upload
  isUploading = false;
  uploadNotFoundSkus: string[] = [];
  /** Bulk SKUs the server refused: they must be broken into child SKUs. */
  uploadBulkSkus: string[] = [];
  /** Counted SKUs whose sheet row carried a decimal — reported, not rounded. */
  uploadFractionalSkus: string[] = [];
  /** Rows whose MRP / CP match no price generation of that SKU. */
  uploadPriceNotFoundSkus: string[] = [];
  /** Rows whose MRP matches several generations — the CP column is needed. */
  uploadAmbiguousPriceSkus: string[] = [];

  // Configuration: negative warehouse stock permitted for Transfer Out. When
  // on, the add-to-table stock blocks below become warnings — the backend
  // applies the same exemption on submit (warehouse sources only).
  allowNegativeStock = false;

  ngOnInit(): void {
    this.viewOnly = this.route.snapshot.queryParamMap.get('view') === 'true';
    this.transferOutId = this.route.snapshot.paramMap.get('id');

    this.configService
      .getValue(SETTING_KEYS.ALLOW_NEGATIVE_WAREHOUSE_STOCK)
      .subscribe((v) => (this.allowNegativeStock = v === true));

    this.loadLocations();
    this.loadReasons();
    // A Transfer Out started from a posted GRN: pre-fill the line items with the
    // GRN's received goods so the warehouse can send them on to a store. The
    // user still picks the destination + reason and reviews quantities.
    const fromGrn = this.route.snapshot.queryParamMap.get('fromGrn');
    if (this.transferOutId) {
      this.isEdit = true;
      this.initDraft(this.transferOutId); // restore is offered once the TO has loaded
      this.loadTransferOut(this.transferOutId);
    } else {
      this.initDraft(null);
      this.getNextNumber();
      if (fromGrn) {
        // The GRN prefill is what the user asked for: no restore question.
        // The prefilled form is copied like any new TO from here on.
        this.draftReady = true;
        this.prefillFromGrn(fromGrn);
      } else {
        // After the first render: the restore question is a modal.
        setTimeout(() => this.offerDraftRestore());
      }
    }

    this.productSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term: string | null) => term !== null && term !== undefined),
        map((term: string) => term.trim()),
        filter((term) => !!term && term.length >= 2),
        tap(() => (this.isSearching = true)),
        switchMap((term) =>
          this.productService
            .searchProductWithContextTansferOut(
              term,
              this.sourceLocationId,
              'TRANSFER_OUT'
            )
            .pipe(
              map((res: any) => res?.data || []),
              catchError((error) => {
                console.error('API error:', error);
                return of([]);
              }),
              tap(() => (this.isSearching = false))
            )
        )
      )
      .subscribe((data) => {
        this.products = data;
      });

    this.productCodeSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => !!term && term.length >= 2),
        tap(() => (this.isCodeSearching = true)),
        switchMap((term) =>
          this.productService
            .searchProducts(
              term,
              this.selectedSearchType,
              this.sourceLocationId,
              'TRANSFER_OUT'
            )
            .pipe(
              map((res: any) => res?.data || []),
              catchError(() => of([])),
              tap(() => (this.isCodeSearching = false))
            )
        )
      )
      .subscribe((data) => {
        this.searchResults = data.map((item: any) => ({
          ...item,
          ean: currentEanOf(item.variants?.[0]),
          displayText: `${item.sku} - ${item.longDescription}`,
        }));
      });

    this.productEanSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => !!term && term.trim().length >= 3),
        tap(() => (this.isEanSearching = true)),
        switchMap((term) =>
          this.productService
            .searchProducts(
              term.trim(),
              'ean',
              this.sourceLocationId,
              'TRANSFER_OUT'
            )
            .pipe(
              map((res: any) => withMatchedEan(res?.data || [], term)),
              catchError(() => of([])),
              tap(() => (this.isEanSearching = false))
            )
        )
      )
      .subscribe((data) => {
        this.eanSearchResults = data;
      });
  }

  @ViewChild('quantityInput') quantityInputRef!: ElementRef;

  ngOnDestroy(): void {
    if (this.draftTimer) clearInterval(this.draftTimer);
    this.autosaveDraft();
  }

  getNextNumber() {
    this.transferOutService
      .getNextTransferOutNumber(this.sourceLocationId || undefined)
      .subscribe((res: any) => {
        this.transferOutNumber = res.transferOutNumber;
      });
  }

  private updateGrandTotal(totalAmount: number = 0) {
    if (this.transferItems.length === 0) {
      this.grandTotal = totalAmount;
    } else {
      this.grandTotal = this.transferItems.reduce(
        (sum, item) => sum + (item.netCost || 0),
        0
      );
    }
  }
  private flattenLocations(locations: LocationNode[]): LocationNode[] {
    let flatList: LocationNode[] = [];

    locations.forEach((location) => {
      // Add the current location itself
      flatList.push({
        id: location.id,
        name: location.name,
        type: location.type,
      });

      // Recursively add children, if they exist
      if (location.childLocations && location.childLocations.length > 0) {
        flatList = flatList.concat(
          this.flattenLocations(location.childLocations)
        );
      }
    });

    return flatList;
  }

  confirmAddItem() {
    if (this.selectedProduct && this.selectedQuantity > 0) {
      this.transferItems.push({
        productVariantId: this.selectedProduct.id,
        quantity: this.selectedQuantity,
      });
      this.selectedProduct = null;
      this.selectedQuantity = 0;
    }
  }

  /**
   * Pre-fill the item list from a posted GRN. Each received line becomes a
   * Transfer Out line (variant + received qty + its pricing) so the operator
   * can forward the just-received goods to a store. Destination, reason and
   * quantities remain fully editable before submit; SOH is not pre-fetched.
   */
  private prefillFromGrn(grnId: string) {
    this.grnService.getGrnById(grnId).subscribe({
      next: (res: any) => {
        const grn = res?.data ?? res;
        const items = grn?.items ?? [];
        if (!items.length) {
          this.toastr.warning('This GRN has no items to transfer.');
          return;
        }
        this.remarks = `From GRN ${grn?.grnNumber ?? ''}`.trim();
        this.transferItems = items.map((item: any, idx: number) => {
          // Free quantity was also received — transfer it along.
          const qty =
            (Number(item.receivedQuantity) || 0) +
            (Number(item.freeQuantity) || 0);
          const costPrice = +item.costPrice || 0;
          return {
            srNo: idx + 1,
            sku: item.skuCode || item.productVariant?.product?.sku || '',
            description:
              item.productVariant?.product?.longDescription ||
              item.description ||
              '',
            quantity: qty,
            costPrice,
            mrp: Number(item.mrp || 0),
            sellingPrice: Number(item.sellingPrice || 0),
            netCost: +(costPrice * qty).toFixed(2),
            soh: 0,
            productVariantId: item.productVariantId,
          };
        });
        this.updateGrandTotal();
        this.toastr.info(
          `Loaded ${this.transferItems.length} item(s) from GRN ${grn?.grnNumber ?? ''}. Pick a destination store and review quantities.`,
        );
      },
      error: () => this.toastr.error('Could not load the GRN to transfer.'),
    });
  }

  removeItem(index: number) {
    this.selectedItems.delete(this.transferItems[index]);
    this.transferItems.splice(index, 1);
    this.renumberItems();
    this.updateGrandTotal();
  }

  // Multi-select line items for bulk delete
  selectedItems = new Set<any>();

  isItemSelected(item: any): boolean {
    return this.selectedItems.has(item);
  }

  toggleItemSelection(item: any): void {
    if (this.selectedItems.has(item)) {
      this.selectedItems.delete(item);
    } else {
      this.selectedItems.add(item);
    }
  }

  get selectedCount(): number {
    return this.transferItems.reduce(
      (n: number, it: any) => n + (this.selectedItems.has(it) ? 1 : 0),
      0
    );
  }

  get allItemsSelected(): boolean {
    return (
      this.transferItems.length > 0 &&
      this.selectedCount === this.transferItems.length
    );
  }

  toggleSelectAll(checked: boolean): void {
    this.selectedItems.clear();
    if (checked) {
      this.transferItems.forEach((it: any) => this.selectedItems.add(it));
    }
  }

  async removeSelectedItems(): Promise<void> {
    const count = this.selectedCount;
    if (!count) return;
    const proceed = await this.confirmDialog.confirm({
      title: 'Delete Selected Items',
      message: `Are you sure you want to delete ${count} selected item${count > 1 ? 's' : ''}?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });
    if (!proceed) return;
    for (let i = this.transferItems.length - 1; i >= 0; i--) {
      if (this.selectedItems.has(this.transferItems[i])) {
        this.transferItems.splice(i, 1);
      }
    }
    this.selectedItems.clear();
    this.renumberItems();
    this.updateGrandTotal();
  }

  private renumberItems(): void {
    this.transferItems.forEach((it: any, idx: number) => (it.srNo = idx + 1));
  }
  loadReasons() {
    this.reasonsService.getAllReasons().subscribe({
      // Assuming the service method is getAllReasons()
      next: (res: any) => {
        // Filter reasons by type 'TO' (Transfer Out) and isActive
        this.reasons = (res || []).filter(
          (r: TransferOutReason) => r.type === 'TO' && r.isActive
        );
      },
      error: (err: any) => {
        console.error('Error fetching transfer out reasons', err);
      },
    });
  }

  loadLocations() {
    this.inventoryService.getAllLocations().subscribe({
      next: (res) => {
        this.locations = res;

        // COMMENT: Use the new helper to create a complete, flat list of ALL possible locations.
        const allPossibleTargets = this.flattenLocations(this.locations);

        // Source options: the locations the user was granted (UserStoreAccess);
        // admins and legacy-unrestricted users get every location. This
        // replaces the old primaryLocationId pin — with a second warehouse the
        // source must be a real choice, not a login-time guess.
        const accessibleIds = new Set(
          this.authService.getStoreAccess().map((a) => a.locationId)
        );
        this.sourceOptions =
          this.authService.isAdmin() || accessibleIds.size === 0
            ? allPossibleTargets
            : allPossibleTargets.filter((loc) => accessibleIds.has(loc.id));

        // Default: keep an already-loaded document's source; else the active
        // location from the header switcher; else legacy primaryLocationId.
        if (!this.sourceLocationId) {
          const userInfo = localStorage.getItem('user');
          const parsedUserInfo = userInfo ? JSON.parse(userInfo) : null;
          const preferred =
            this.authService.getActiveLocationId() ||
            parsedUserInfo?.primaryLocationId;
          if (preferred && this.sourceOptions.some((l) => l.id === preferred)) {
            this.sourceLocationId = preferred;
          } else if (this.sourceOptions.length === 1) {
            this.sourceLocationId = this.sourceOptions[0].id;
          }
        }

        this.onSourceChanged();

        console.log(
          `Total potential target locations: ${this.filteredTargetLocations.length}`
        );
      },
      error: (err) => {
        console.error('Error fetching locations', err);
      },
    });
  }

  onSourceChanged() {
    // COMMENT: When the source changes, refilter the complete list of locations.
    const allPossibleTargets = this.flattenLocations(this.locations);

    this.filteredTargetLocations = allPossibleTargets.filter(
      (loc) => loc.id !== this.sourceLocationId
    );

    // COMMENT: Ensure the target is cleared if it's the same as the new source.
    if (this.sourceLocationId === this.targetLocationId) {
      this.targetLocationId = '';
    }

    // The TO number prefix follows the source location's store code.
    if (!this.transferOutId && this.sourceLocationId) {
      this.getNextNumber();
    }
  }

  onProductSelected(product: any) {
    if (!product) return;

    this.selectedProduct = product;
    this.selectedSearchResult = this.searchResults.find(
      (p) => p.sku === product.sku
    );

    this.selectedProduct = product;
    this.selectedVariant = product?.variants?.[0] || null;
    this.selectedQuantity = product?.innerPackaging || 1;

    // Populate cost & MRP options (defaults to the newest generation).
    this.populatePriceOptions(product);
    // show toast + focus
    this.toastr.success(
      `Product selected: ${product.longDescription}`,
      'Product Selected'
    );
    setTimeout(() => {
      this.quantityInputRef?.nativeElement?.focus();
    }, 100);

    // Clear the SKU ng-select model quickly so later typing triggers API again.
    setTimeout(() => {
      this.searchResults = [];
    }, 300);
  }

  /** Warehouse stock-on-hand of the currently selected product (pick row). */
  get selectedSoh(): number | null {
    const v = this.selectedProduct?.variants?.[0];
    if (!v) return null;
    return Number(v.soh ?? 0);
  }

  async confirmAddToTable() {
    if (!this.selectedProduct || this.selectedQuantity <= 0) return;

    const variant = this.selectedProduct.variants?.[0];
    // The generation the Cost + MRP dropdowns name — not merely the newest.
    const price = this.selectedPricing ?? variant?.pricing?.[0];
    const soh = variant?.soh || 0;
    const productVariantId = variant?.id;

    if (!productVariantId) {
      this.toastr.error('Invalid product variant');
      return;
    }

    if (soh === 0 && !this.allowNegativeStock) {
      this.toastr.warning('Item is Out Of Stock');
      return;
    }

    if (this.selectedQuantity > soh) {
      if (!this.allowNegativeStock) {
        this.toastr.error(
          `Quantity exceeds available stock at warehouse. Available: ${soh}`
        );
        return;
      }
      this.toastr.warning(
        `Quantity exceeds available stock (${soh}) — warehouse stock will go negative.`
      );
    }

    // Lines are keyed by (SKU, price generation): the same SKU at another
    // MRP / CP is a different line, exactly as on a PO or GRN.
    const samePriceLine = this.transferItems.find(
      (item) =>
        item.productVariantId === productVariantId &&
        (item.variantPricingId ?? null) === (price?.id ?? null),
    );
    const otherPriceLine = samePriceLine
      ? null
      : this.transferItems.find((item) => item.productVariantId === productVariantId);
    const existing = samePriceLine ?? otherPriceLine;

    if (existing) {
      const choice = await this.confirmDialog.choose(
        samePriceLine
          ? {
              title: 'Item already added',
              message: `<strong>${this.selectedProduct.sku}</strong> is already in the table with quantity <strong>${existing.quantity}</strong>.<br/>Add <strong>${this.selectedQuantity}</strong> to the existing quantity, or replace it?`,
              confirmText: 'Add Qty',
              altText: 'Replace Qty',
              cancelText: 'Cancel',
            }
          : {
              title: 'Same SKU, different price',
              message:
                `<strong>${this.selectedProduct.sku}</strong> is already in the table at ` +
                `<strong>${this.priceLabel(existing)}</strong> (qty <strong>${existing.quantity}</strong>).<br/>` +
                `You are adding <strong>${this.selectedQuantity}</strong> at <strong>${this.priceLabel(price)}</strong>.<br/>` +
                `Add it as a new line at this price, or merge into the existing line?`,
              confirmText: 'Add Qty',
              altText: 'Replace Qty',
              alt2Text: 'Add as New Line',
              cancelText: 'Cancel',
              size: 'lg',
            },
      );
      if (choice === 'cancel') return;

      if (choice === 'alt2') {
        // Falls through to the push below: a second line for this SKU, at
        // its own price. Stock is checked across every line of the variant.
        const onDoc = this.transferItems
          .filter((item) => item.productVariantId === productVariantId)
          .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
        if (onDoc + this.selectedQuantity > soh) {
          if (!this.allowNegativeStock) {
            this.toastr.error(
              `Quantity across all lines of this SKU exceeds available stock at warehouse. Available: ${soh}`,
            );
            return;
          }
          this.toastr.warning(
            `Quantity across all lines of this SKU exceeds available stock (${soh}) — warehouse stock will go negative.`,
          );
        }
      } else {
        // Merge into the existing line (at THAT line's price).
        const newQty =
          choice === 'confirm'
            ? (+existing.quantity || 0) + this.selectedQuantity
            : this.selectedQuantity;

        if (newQty > soh) {
          if (!this.allowNegativeStock) {
            this.toastr.error(
              `Quantity exceeds available stock at warehouse. Available: ${soh}`
            );
            return;
          }
          this.toastr.warning(
            `Quantity exceeds available stock (${soh}) — warehouse stock will go negative.`
          );
        }

        existing.quantity = newQty;
        existing.netCost = parseFloat(
          ((+existing.costPrice || 0) * newQty).toFixed(2)
        );
        this.updateGrandTotal();
        this.toastr.info(
          `Quantity ${choice === 'confirm' ? 'updated' : 'replaced'} for ${existing.sku}`,
          'Item Merged'
        );

        this.selectedProduct = null;
        this.selectedQuantity = 1;
        return;
      }
    }

    this.transferItems.push({
      srNo: this.transferItems.length + 1,
      sku: this.selectedProduct.sku,
      description: this.selectedProduct.name,
      quantity: this.selectedQuantity,
      // Carried onto the line so its quantity box knows whether decimals are
      // legal for this SKU (kg/gm/ltr, bulk, weighable, bulk child).
      uom: this.selectedProduct.uom ?? null,
      isBulk: this.selectedProduct.isBulk ?? null,
      isWeighable: this.selectedProduct.isWeighable ?? null,
      bulkParentProductId: this.selectedProduct.bulkParentProductId ?? null,
      costPrice: price?.costPrice ? +price.costPrice : 0,
      mrp: price?.mrp ? +price.mrp : 0,
      netCost: parseFloat(
        (+price?.costPrice * this.selectedQuantity).toFixed(2)
      ),
      sellingPrice: +price?.sellingPrice || 0,
      soh: variant?.soh || 0,
      productVariantId: variant?.id,
      variantPricingId: price?.id ?? null,
    });
    this.updateGrandTotal();

    this.selectedProduct = null;
    this.selectedQuantity = 1;
  }

  getNetTotal(): number {
    return this.transferItems.reduce(
      (sum, item) => sum + (item.netCost || 0),
      0
    );
  }
  /**
   * What the server will refuse anyway, said here first so the user is not
   * shown a confirm dialog and then a "must be a UUID" error. Returns false
   * (after a toast) when the document is not ready to leave.
   */
  private validateHeaderAndLines(): boolean {
    if (!this.sourceLocationId) {
      this.toastr.error('Source location is not set.');
      return false;
    }
    if (!this.targetLocationId) {
      this.toastr.error('Select the destination store (To Location).');
      return false;
    }
    if (this.targetLocationId === this.sourceLocationId) {
      this.toastr.error('Destination must be different from the source location.');
      return false;
    }
    if (!this.transferItems.length) {
      this.toastr.error('Add at least one item to the transfer.');
      return false;
    }
    const bad = this.transferItems.find((i) => !(Number(i.quantity) > 0));
    if (bad) {
      this.toastr.error(`Quantity for ${bad.sku} must be greater than zero.`);
      return false;
    }
    return true;
  }

  async submitTransferOut() {
    if (!this.validateHeaderAndLines()) return;

    // Check source location type
    const allLocations = this.flattenLocations(this.locations);
    const sourceLocation = allLocations.find(
      (l) => l.id === this.sourceLocationId
    );
    const isSourceWarehouse =
      sourceLocation?.type?.toLowerCase() === 'warehouse';

    if (!this.selectedReasonId && !isSourceWarehouse) {
      // COMMENT: Validation for Reason selection
      this.toastr.error('Please select a Reason for Transfer Out.');
      return;
    }

    // Confirm before dispatch — submitting moves stock into transit.
    const totalQty = this.getTotalQuantity();
    const targetName =
      allLocations.find((l) => l.id === this.targetLocationId)?.name ??
      'the selected destination';

    // GST Rule 138: say BEFORE the goods are committed whether this
    // consignment will need an e-way bill, on the same valuation the bill
    // itself uses. Purely informative — a failed estimate never blocks.
    const ewayNote = await this.ewayBillEstimateNote();

    const proceed = await this.confirmDialog.confirm({
      title: this.transferOutId ? 'Update Transfer Out?' : 'Submit Transfer Out?',
      message:
        `You are about to send <strong>${totalQty}</strong> unit(s) across ` +
        `<strong>${this.transferItems.length}</strong> item(s) to ` +
        `<strong>${targetName}</strong>.<br>` +
        `Stock will move out of ${sourceLocation?.name ?? 'the source location'} and into transit.` +
        ewayNote,
      confirmText: this.transferOutId ? 'Update' : 'Submit',
      cancelText: 'Cancel',
      size: 'lg',
    });
    if (!proceed) return;

    let grandTotal = this.isEdit ? this.updateGrandTotal() : 0;
    const request = {
      sourceLocationId: this.sourceLocationId,
      // The document number is assigned by the server at create. The number
      // shown on screen is a non-reserving preview, so it is not sent.
      targetLocationId: this.targetLocationId,
      remarks: this.remarks,
      status: 'IN_TRANSIT',
      reasonId: this.selectedReasonId,
      totalQty: this.getTotalQuantity(),
      totalAmount: this.transferItems.reduce(
        (sum, item) => sum + (Number(item.netCost) || 0),
        0
      ),
      items: this.transferItems.map((item) => ({
        productVariantId: item.productVariantId,
        quantity: item.quantity,
        variantPricingId: item.variantPricingId ?? undefined,
      })),
    };

    const request$ = this.transferOutId
      ? this.transferOutService.updateTransferOut(this.transferOutId, request)
      : this.transferOutService.createTransferOut(request);

    request$.subscribe({
      next: (response: any) => {
        this.discardDraft();
        const numberToShow = response.transferOutNumber || '';
        if (numberToShow) this.transferOutNumber = numberToShow;
        this.toastr.success(
          ` ${numberToShow} Transfer Out created successfully`,
          `Success`
        );

        const createdId = response?.id || response?.data?.id || this.transferOutId;

        // The server assessed the saved document: if it needs an e-way bill,
        // offer to raise it right now with this TO preselected. This is the
        // moment it matters — the goods are committed and not yet on the road.
        const eway = response?.ewayBill;
        if (eway?.required && createdId) {
          this.promptEwayBill(createdId, numberToShow, eway);
          return;
        }

        if (createdId) {
          this.loadPreview(createdId);
        } else {
          this.afterSave();
        }
      },
      error: (err) => {
        console.error(err);
        alert('Something went wrong');
      },
    });
    this.isSaved = true;
  }

  /**
   * Line for the submit confirmation: "an e-way bill will be required" (or
   * not), from the server's estimate of this screen's lines. Empty when the
   * estimate is unavailable — the post-save prompt still catches it.
   */
  private async ewayBillEstimateNote(): Promise<string> {
    try {
      const q: any = await firstValueFrom(
        this.transferOutService.quoteEwayBill({
          sourceLocationId: this.sourceLocationId,
          targetLocationId: this.targetLocationId,
          items: this.transferItems.map((i) => ({
            productVariantId: i.productVariantId,
            quantity: Number(i.quantity) || 0,
            variantPricingId: i.variantPricingId ?? null,
          })),
        }),
      );
      const money = (n: number) =>
        `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
      if (q?.intraPremises) {
        return (
          `<br><br><small class="text-muted">Goods stay on the premises (damage / expiry area) — ` +
          `no e-way bill applies.</small>`
        );
      }
      if (q?.required) {
        return (
          `<br><br><span class="text-danger"><i class="bi bi-exclamation-triangle-fill"></i> ` +
          `<strong>E-way bill required.</strong> Consignment value ${money(q.consignmentValue)} (incl. GST) ` +
          `is above the ${money(q.threshold)} threshold — raise it before the vehicle leaves.` +
          (q.gstinMissing ? ' <em>A store GSTIN is missing; fix that first.</em>' : '') +
          `</span>`
        );
      }
      return (
        `<br><br><small class="text-muted">Consignment value ${money(q?.consignmentValue)} (incl. GST) ` +
        `is within the ${money(q?.threshold)} e-way bill threshold — no bill required.</small>`
      );
    } catch {
      return '';
    }
  }

  /** After save: raise the bill now (TO preselected) or view the challan. */
  private async promptEwayBill(
    createdId: string,
    number: string,
    eway: { consignmentValue: number; threshold: number },
  ): Promise<void> {
    const money = (n: number) =>
      `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
    const now = await this.confirmDialog.confirm({
      title: 'E-way bill required',
      message:
        `<strong>${number}</strong> has a consignment value of <strong>${money(eway.consignmentValue)}</strong> ` +
        `(incl. GST), above the ${money(eway.threshold)} threshold.<br>` +
        `Under GST Rule 138 an e-way bill must be generated before the goods move.<br><br>` +
        `Create it now? You can also do it later from the Transfer Out list, where it stays flagged.`,
      confirmText: 'Create E-Way Bill',
      cancelText: 'Later',
      size: 'lg',
    });
    if (now) {
      this.router.navigate(['/transfer-out/eway-bill/create'], {
        queryParams: { stockTransferId: createdId },
      });
    } else {
      this.loadPreview(createdId);
    }
  }

  loadTransferOut(id: string) {
    this.transferOutService.getTransferOutById(id).subscribe((res: any) => {
      console.log(`res: ${JSON.stringify(res)}`);
      this.sourceLocationId = res.sourceLocationId;
      this.targetLocationId = res.targetLocationId;
      this.transferOutNumber = res.transferOutNumber;
      this.remarks = res.remarks;
      this.totalAmount = res.totalAmount;
      this.selectedReasonId = res.reasonId;
      console.log(`selectedReasonId: ${this.selectedReasonId}`);
      if (this.selectedReasonId && this.reasons.length > 0) {
        const selectedReason = this.reasons.find(
          (r) => r.id === this.selectedReasonId
        );
        console.log(`selectedReason: ${JSON.stringify(selectedReason)}`);
        this.selectedReasonDescription = selectedReason?.shortReason || 'N/A';
      } else if (this.selectedReasonId && this.reasons.length === 0) {
        // If reasons haven't loaded yet, try again after a small delay
        setTimeout(() => {
          const selectedReason = this.reasons.find(
            (r) => r.id === this.selectedReasonId
          );
          this.selectedReasonDescription = selectedReason?.shortReason || 'N/A';
        }, 500);
      } else {
        this.selectedReasonDescription = 'N/A';
      }

      this.transferItems = res.items.map((item: any, i: number) => {
        const variant = item.productVariant || {};
        const product = variant.product || {};
        // The generation the line was saved with; the variant's newest price
        // is only the fallback for legacy lines that never named one.
        const pricing =
          item.variantPricing ??
          (Array.isArray(variant.pricing) && variant.pricing.length > 0
            ? variant.pricing[0]
            : {});

        const costPrice = pricing.costPrice ? +pricing.costPrice : 0;
        // Coerce to a number — the API may return quantity as a string, which
        // makes `+` concatenate ("30" + "20" -> "3020") in the qty totals.
        const quantity = Number(item.quantity) || 0;

        return {
          srNo: i + 1,
          id: item.id,
          productVariantId: item.productVariantId,
          sku: product.sku || '',
          description: variant.variantName || '',
          quantity: quantity,
          variantPricingId: item.variantPricingId ?? pricing?.id ?? null,
          uom: product.uom ?? null,
          isBulk: product.isBulk ?? null,
          isWeighable: product.isWeighable ?? null,
          bulkParentProductId: product.bulkParentProductId ?? null,
          costPrice: costPrice,
          mrp: pricing.mrp ? +pricing.mrp : null,
          sellingPrice: pricing.sellingPrice ? +pricing.sellingPrice : null,
          basicCost: pricing.basePrice ? +pricing.basePrice : null,
          soh: variant.soh ?? null,
          netCost: parseFloat((costPrice * quantity).toFixed(2))
        };
      });

      this.updateGrandTotal();
      this.offerDraftRestore(res?.updatedAt);
    });
  }

  /**
   * Sum of line quantities at the stored precision. Fractional (kg) lines
   * make a raw float sum drift (4777.990000000001), which the API rejects
   * as more than 3 decimals — so always round here.
   */
  getTotalQuantity(): number {
    return roundQty(
      this.transferItems.reduce(
        (sum, item) => sum + (Number(item.quantity) || 0),
        0
      )
    );
  }

  saveAsDraft() {
    if (!this.validateHeaderAndLines()) return;
    let totalAmount = this.isEdit ? this.updateGrandTotal() : 0;
    const draftRequest = {
      sourceLocationId: this.sourceLocationId,
      // The document number is assigned by the server at create. The number
      // shown on screen is a non-reserving preview, so it is not sent.
      targetLocationId: this.targetLocationId,
      remarks: this.remarks,
      status: 'DRAFT',
      reasonId: this.selectedReasonId,
      totalQty: this.getTotalQuantity(),
      totalAmount: this.transferItems.reduce(
        (sum, item) => sum + (Number(item.netCost) || 0),
        0
      ),
      items: this.transferItems.map((item) => ({
        productVariantId: item.productVariantId,
        quantity: item.quantity,
        variantPricingId: item.variantPricingId ?? undefined,
      })),
    };

    const draftRequest$ = this.transferOutId
      ? this.transferOutService.updateTransferOut(
        this.transferOutId,
        draftRequest
      )
      : this.transferOutService.createTransferOut(draftRequest);

    draftRequest$.subscribe({
      next: (response: any) => {
        this.discardDraft();
        const numberToShow = response.transferOutNumber;
        if (numberToShow) this.transferOutNumber = numberToShow;
        this.toastr.success(
          ` ${numberToShow} Draft saved successfully`,
          'Success'
        );
        this.isSaved = true;

        const createdId = response?.id || response?.data?.id || this.transferOutId;
        if (createdId) {
          this.loadPreview(createdId);
        } else {
          this.afterSave();
        }
      },
      error: (err) => {
        console.error(err);
        alert('Failed to save draft');
      },
    });
  }

  onSearchResultSelected(product: any) {
    if (!product) return;
    this.selectedSearchResult = product;
    this.selectedProduct = product;
    this.selectedProduct = this.products.find((p) => p.sku === product.sku);
    this.selectedSearchResult = product;
    this.selectedProduct = product;
    this.selectedVariant = product.variants?.[0];
    this.selectedQuantity = product?.innerPackaging || 1;
    this.populatePriceOptions(product);

    // Focus on quantity input after selection
    setTimeout(() => {
      this.quantityInputRef?.nativeElement?.focus();
    }, 100);

    this.toastr.success(
      `Product found: ${product.longDescription}`,
      'Product Selected'
    );
    setTimeout(() => {
      this.quantityInputRef?.nativeElement?.focus();
    }, 100);

    // Clear the SKU ng-select model after a short delay so next typing triggers API again.
    // We keep selectedProduct (the real product) so the add/qty UI remains populated.
    setTimeout(() => {
      this.selectedSearchResult = null;
    }, 250);
  }

  // ✅ NEW: Handle search type change (SKU/PLU/EAN/General)
  onSearchTypeChange() {
    this.searchResults = [];
    this.eanSearchResults = [];
    // Clear current selection when search type changes
    if (this.selectedProduct) {
      this.selectedProduct = null;
    }
  }

  populatePriceOptions(products: any) {
    this.priceOptions = [];
    this.allCostPrices = [];
    this.allMRPs = [];

    const variant = products?.variants?.[0];
    // Retired generations are not offered — nothing ships at a dead price.
    this.priceOptions = (variant?.pricing ?? []).filter(
      (price: any) => price.isActive !== false,
    );

    // Both dropdowns list EVERY value the SKU is priced at, so a second MRP
    // is visible at a glance; picking one side snaps the other to a
    // generation that actually carries it (see onCostPriceChange/onMrpChange).
    for (const price of this.priceOptions) {
      const costPriceNum = price.costPrice ? +price.costPrice : 0;
      const mrpNum = price.mrp ? +price.mrp : 0;
      if (costPriceNum > 0 && !this.allCostPrices.some((c) => this.samePrice(c, costPriceNum))) {
        this.allCostPrices.push(costPriceNum);
      }
      if (mrpNum > 0 && !this.allMRPs.some((m) => this.samePrice(m, mrpNum))) {
        this.allMRPs.push(mrpNum);
      }
    }

    const first = this.priceOptions[0];
    this.enteredCostPrice = first ? +first.costPrice || 0 : 0;
    this.selectedMRP = first ? +first.mrp || 0 : 0;
  }

  private samePrice(a: unknown, b: unknown): boolean {
    return Math.abs(Number(a) - Number(b)) < 0.005;
  }

  /**
   * Cost changed: if no generation carries this cost together with the
   * current MRP, move the MRP to one that does — the pair must always name
   * a real pricing row.
   */
  onCostPriceChange(): void {
    const cost = Number(this.enteredCostPrice);
    const fits = this.priceOptions.some(
      (p) => this.samePrice(p.costPrice, cost) && this.samePrice(p.mrp, this.selectedMRP),
    );
    if (fits) return;
    const match = this.priceOptions.find((p) => this.samePrice(p.costPrice, cost));
    if (match) this.selectedMRP = +match.mrp || 0;
  }

  /** MRP changed: same in the other direction. */
  onMrpChange(): void {
    const mrp = Number(this.selectedMRP);
    const fits = this.priceOptions.some(
      (p) => this.samePrice(p.mrp, mrp) && this.samePrice(p.costPrice, this.enteredCostPrice),
    );
    if (fits) return;
    const match = this.priceOptions.find((p) => this.samePrice(p.mrp, mrp));
    if (match) this.enteredCostPrice = +match.costPrice || 0;
  }

  /** The pricing row the Cost + MRP dropdowns currently name. */
  get selectedPricing(): any | null {
    const same = (a: unknown, b: unknown) =>
      Math.abs(Number(a) - Number(b)) < 0.005;
    return (
      this.priceOptions.find(
        (p) => same(p.costPrice, this.enteredCostPrice) && same(p.mrp, this.selectedMRP),
      ) ??
      this.priceOptions.find((p) => same(p.costPrice, this.enteredCostPrice)) ??
      this.priceOptions[0] ??
      null
    );
  }

  /** "MRP 130 / CP 100" — how a line's price generation is named to the user. */
  private priceLabel(line: { mrp?: any; costPrice?: any }): string {
    return `MRP ${Number(line?.mrp ?? 0)} / CP ${Number(line?.costPrice ?? 0)}`;
  }
  // ── Excel Template & Upload ──────────────────────────────────────

  downloadTemplate() {
    this.transferOutService.downloadTemplate().subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'transfer_out_template.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.toastr.success('Template downloaded successfully.');
      },
      error: () => {
        this.toastr.error('Failed to download template.');
      },
    });
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    // Reset input so same file can be re-selected
    input.value = '';

    if (!this.sourceLocationId) {
      this.toastr.error('Source location is not set. Cannot upload.');
      return;
    }

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    if (!validTypes.includes(file.type) && !file.name.match(/\.xlsx?$/i)) {
      this.toastr.error('Please upload a valid Excel file (.xlsx or .xls)');
      return;
    }

    this.isUploading = true;
    this.uploadNotFoundSkus = [];
    this.uploadBulkSkus = [];
    this.uploadFractionalSkus = [];
    this.uploadPriceNotFoundSkus = [];
    this.uploadAmbiguousPriceSkus = [];

    this.transferOutService
      .uploadExcel(file, this.sourceLocationId)
      .subscribe({
        next: (res: any) => {
          this.isUploading = false;
          const uploadedItems = res.items || [];
          this.uploadNotFoundSkus = res.notFoundSkus || [];
          this.uploadBulkSkus = res.bulkSkus || [];
          this.uploadFractionalSkus = res.fractionalSkus || [];
          this.uploadPriceNotFoundSkus = res.priceNotFoundSkus || [];
          this.uploadAmbiguousPriceSkus = res.ambiguousPriceSkus || [];

          if (uploadedItems.length === 0) {
            // Bulk-only file: say why nothing came through, otherwise the
            // generic "no matching products" reads like the SKUs were wrong.
            if (this.uploadBulkSkus.length > 0) {
              this.toastr.warning(
                `Bulk SKU(s) cannot be transferred: ${this.uploadBulkSkus.join(', ')}. Break them into child SKUs first.`,
                'Bulk SKUs Skipped',
                { timeOut: 10000 },
              );
              return;
            }
            this.toastr.warning('No matching products found in the uploaded file.');
            return;
          }

          let addedCount = 0;
          let skippedCount = 0;

          for (const item of uploadedItems) {
            // Skip if already in the table AT THIS PRICE. The same SKU at
            // another MRP / CP is its own line — keying this on the SKU
            // alone is what dropped the second row of a two-price SKU.
            const alreadyAdded = this.transferItems.some(
              (existing) =>
                existing.productVariantId === item.productVariantId &&
                (existing.variantPricingId ?? null) === (item.variantPricingId ?? null),
            );
            if (alreadyAdded) {
              skippedCount++;
              continue;
            }

            this.transferItems.push({
              srNo: this.transferItems.length + 1,
              sku: item.skuCode,
              description: item.productName,
              quantity: item.quantity,
              // Resolved server-side from the product's uom/bulk/weighable
              // flags, so the imported line keeps its decimals editable.
              uom: item.uom ?? null,
              allowsFraction: item.allowsFraction ?? false,
              variantPricingId: item.variantPricingId ?? null,
              costPrice: item.costPrice || 0,
              sellingPrice: item.sellingPrice || 0,
              mrp: item.mrp || 0,
              netCost: parseFloat(
                ((item.costPrice || 0) * item.quantity).toFixed(2)
              ),
              soh: item.stockQuantity || 0,
              productVariantId: item.productVariantId,
            });
            addedCount++;
          }

          this.updateGrandTotal();

          // Show summary
          let message = `${addedCount} item(s) added from Excel.`;
          if (skippedCount > 0) {
            message += ` ${skippedCount} duplicate(s) skipped.`;
          }
          this.toastr.success(message);

          if (this.uploadNotFoundSkus.length > 0) {
            this.toastr.warning(
              `SKUs not found: ${this.uploadNotFoundSkus.join(', ')}`,
              'Some SKUs Missing',
              { timeOut: 8000 }
            );
          }

          if (this.uploadBulkSkus.length > 0) {
            this.toastr.warning(
              `Bulk SKU(s) skipped: ${this.uploadBulkSkus.join(', ')}. Break them into child SKUs first.`,
              'Bulk SKUs Skipped',
              { timeOut: 10000 },
            );
          }

          if (this.uploadFractionalSkus.length > 0) {
            this.toastr.warning(
              `Decimal quantity is not allowed for: ${this.uploadFractionalSkus.join(', ')}. ` +
                'These items are counted in whole units — fix the sheet and upload again.',
              'Decimal Quantities Skipped',
              { timeOut: 10000 },
            );
          }

          if (this.uploadPriceNotFoundSkus.length > 0) {
            this.toastr.warning(
              `No such price for: ${this.uploadPriceNotFoundSkus.join(', ')}. ` +
                'Check the MRP / COST_PRICE columns against the SKU\'s current prices.',
              'Price Not Found',
              { timeOut: 10000 },
            );
          }

          if (this.uploadAmbiguousPriceSkus.length > 0) {
            this.toastr.warning(
              `More than one price matches: ${this.uploadAmbiguousPriceSkus.join(', ')}. ` +
                'Fill the COST_PRICE column for these rows to pick one.',
              'Price Ambiguous',
              { timeOut: 10000 },
            );
          }
        },
        error: (err) => {
          this.isUploading = false;
          const msg =
            err?.error?.message || 'Failed to process the uploaded file.';
          this.toastr.error(msg);
        },
      });
  }

  async canDeactivate(): Promise<boolean> {
    // If the form has been successfully saved, allow deactivation
    if (this.isSaved) {
      return true;
    }
    if (this.isEdit) {
      const leave = await this.confirmDialog.confirm({
        title: 'Leave Edit Page?',
        message:
          'Are you sure you want to leave <strong>Edit Transfer Out</strong>  page?',
        confirmText: 'Leave',
        cancelText: 'Stay',
        size: 'lg',
      });
      // Leaving on purpose discards the browser copy too. Not when this is
      // the sign-out path (no token by now): that copy is the user's way back.
      if (leave && this.authService.isLoggedIn()) this.discardDraft();
      return leave;
    }
    if (this.isSaved) {
      return true;
    }

    // Check for unsaved changes by looking at the form state
    // This is a more comprehensive check than your original code
    this.hasUnsavedChanges =
      this.transferItems.length > 0 ||
      !!this.sourceLocationId ||
      !!this.targetLocationId ||
      !!this.remarks ||
      this.products.length > 0;

    if (this.hasUnsavedChanges) {
      const leave = await this.confirmDialog.confirm({
        title: 'Discard changes?',
        message:
          'You have unsaved changes in <strong>Transfer Out</strong>. Are you sure you want to leave this page?',
        confirmText: 'Leave',
        cancelText: 'Stay',
        size: 'lg',
      });
      if (leave && this.authService.isLoggedIn()) this.discardDraft();
      return leave;
    }

    return true;
  }

  // ── Unsaved-work safety copy ────────────────────────────────────────────
  // Same protection as the GRN form (a 140-line GRN was lost to an expired
  // session): the form copies itself to the browser every few seconds
  // (FormDraftStore) and offers the copy back when it is opened again.
  private draftStore: FormDraftStore<TransferOutDraftSnapshot> | null = null;
  private draftTimer: ReturnType<typeof setInterval> | null = null;
  /** False until the restore question is settled, so the still-blank form
   *  cannot overwrite the copy it is about to offer. */
  private draftReady = false;
  /** Saved to the server, or discarded on purpose: stop copying. */
  private draftClosed = false;

  private initDraft(transferOutId: string | null): void {
    if (this.viewOnly) return;
    this.draftStore = new FormDraftStore<TransferOutDraftSnapshot>([
      'to',
      this.authService.getUserId(),
      this.authService.getActiveLocationId(),
      transferOutId ?? 'new',
    ]);
    // Outside Angular: the timer must not trigger change detection.
    this.zone.runOutsideAngular(() => {
      this.draftTimer = setInterval(() => this.autosaveDraft(), 5000);
    });
  }

  private hasDraftContent(d: Partial<TransferOutDraftSnapshot> | null | undefined): boolean {
    // The source follows the header switcher, so on its own it is not content.
    return !!(d?.transferItems?.length || d?.targetLocationId);
  }

  private draftSnapshot(): TransferOutDraftSnapshot {
    const target = this.filteredTargetLocations.find(
      (l: any) => l.id === this.targetLocationId,
    );
    return {
      sourceLocationId: this.sourceLocationId,
      targetLocationId: this.targetLocationId,
      targetLocation: target ? { id: target.id, name: target.name } : null,
      selectedReasonId: this.selectedReasonId,
      remarks: this.remarks,
      transferItems: this.transferItems,
    };
  }

  /** Also on pagehide: a closed tab or browser keeps the last few seconds. */
  @HostListener('window:pagehide')
  autosaveDraft(): void {
    if (!this.draftStore || !this.draftReady || this.draftClosed) return;
    const snapshot = this.draftSnapshot();
    // An emptied form leaves any earlier copy alone ("Not now" kept it).
    if (this.hasDraftContent(snapshot)) this.draftStore.save(snapshot);
  }

  private discardDraft(): void {
    this.draftClosed = true;
    this.draftStore?.clear();
  }

  /**
   * Offer a copy left by an earlier visit. For an existing TO a copy older
   * than the version on the server is stale and silently dropped.
   */
  private async offerDraftRestore(serverUpdatedAt?: string): Promise<void> {
    const stored = this.draftStore?.load();
    if (!stored || !this.hasDraftContent(stored.data)) {
      this.draftReady = true;
      return;
    }
    if (serverUpdatedAt && stored.savedAt <= Date.parse(serverUpdatedAt)) {
      this.draftStore!.clear();
      this.draftReady = true;
      return;
    }

    const d = stored.data;
    const when = new Date(stored.savedAt).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    const lines = d.transferItems?.length ?? 0;
    const details = [
      d.targetLocation?.name ? `to <strong>${escapeHtml(d.targetLocation.name)}</strong>` : '',
      `<strong>${lines}</strong> line${lines === 1 ? '' : 's'}`,
    ].filter(Boolean);
    const choice = await this.confirmDialog.choose({
      title: 'Restore unsaved Transfer Out?',
      message:
        `This Transfer Out was not saved. A copy from ${when} is still on this computer: ${details.join(', ')}.` +
        '<br><small class="text-muted">"Not now" keeps the copy, but it is replaced once you start entering a Transfer Out here.</small>',
      confirmText: 'Restore',
      altText: 'Discard',
      cancelText: 'Not now',
      size: 'lg',
    });
    if (choice === 'confirm') this.applyDraft(d);
    else if (choice === 'alt') this.draftStore!.clear();
    this.draftReady = true;
  }

  private applyDraft(d: TransferOutDraftSnapshot): void {
    // Make sure the destination picker can show the restored value; once the
    // locations arrive onSourceChanged rebuilds the list properly.
    const target = d.targetLocation;
    if (
      !this.locations.length &&
      target?.id &&
      !this.filteredTargetLocations.some((l: any) => l.id === target.id)
    ) {
      this.filteredTargetLocations = [...this.filteredTargetLocations, target];
    }

    if (d.sourceLocationId && d.sourceLocationId !== this.sourceLocationId) {
      this.sourceLocationId = d.sourceLocationId;
      if (this.locations.length) this.onSourceChanged();
    }
    this.targetLocationId = d.targetLocationId;
    this.selectedReasonId = d.selectedReasonId;
    this.remarks = d.remarks;
    this.transferItems = d.transferItems ?? [];
    this.selectedItems.clear();

    this.updateGrandTotal();
    this.toastr.success(
      `${this.transferItems.length} line(s) restored. Save Draft to keep them on the server.`,
      'Transfer Out restored',
    );
  }

  /**
   * Whether this line may be keyed in fractions — bulk, weighable, bulk-child
   * or a kg/gm/ltr unit. Drives the quantity box and the minimum below; the
   * server applies the same rule on save.
   */
  allowsDecimal(source: any): boolean {
    return allowsDecimalQty(source);
  }

  /** Smallest quantity this line may carry: 1 pc, or 1 gram/ml. */
  minQty(source: any): number {
    return this.allowsDecimal(source) ? 0.001 : 1;
  }

  updateItemQuantity(index: number) {
    const item = this.transferItems[index];
    const allowDecimal = this.allowsDecimal(item);
    const min = allowDecimal ? 0.001 : 1;

    item.quantity = allowDecimal
      ? roundQty(item.quantity)
      : Math.round(Number(item.quantity) || 0);

    if (!(item.quantity > 0)) {
      // A weighable line legitimately sits below 1 (0.5 kg), so the floor is
      // the smallest storable quantity, not a whole unit.
      this.toastr.warning(`Quantity must be at least ${min}`);
      item.quantity = min;
    }

    // Recalculate netCost based on updated quantity
    item.netCost = (item.costPrice || 0) * item.quantity;

    // Update totals
    this.updateGrandTotal();
  }

  getSourceLocationName(): string {
    // sourceOptions is the flattened, access-scoped list; this.locations only
    // holds root nodes, so look there first.
    const match =
      this.sourceOptions.find((loc) => loc.id === this.sourceLocationId) ??
      this.locations.find((loc) => loc.id === this.sourceLocationId);
    return match?.name || 'N/A';
  }

  loadPreview(id: string) {
    this.showPreview = true;
    this.isLoadingPreview = true;

    this.transferOutService.downloadTransferOut(id).subscribe({
      next: (response: any) => {
        const blob = response.body;
        if (!blob || blob.size === 0) {
          console.error('Empty PDF blob received');
          this.toastr.error('Received empty PDF');
          this.isLoadingPreview = false;
          return;
        }
        const url = URL.createObjectURL(blob);
        this.rawBlobUrl = url;
        this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          url + '#toolbar=0&navpanes=0&view=FitH'
        );
        this.isLoadingPreview = false;
      },
      error: (err) => {
        console.error('Failed to download Transfer Out PDF', err);
        this.toastr.error('Failed to load preview');
        this.isLoadingPreview = false;
      },
    });
  }

  closePreview() {
    this.showPreview = false;
    // Clean up blob URL
    if (this.rawBlobUrl) {
      URL.revokeObjectURL(this.rawBlobUrl);
      this.rawBlobUrl = null;
    }
    this.afterSave();
  }

  /**
   * Where to go once a save is done (preview closed, or no preview). The
   * warehouse raises several transfer outs back to back, so a new TO comes
   * back to a blank create form (the plain create URL, so a GRN prefill is
   * not replayed); an edit returns to the list as before.
   */
  private afterSave(): void {
    if (this.transferOutId) {
      this.router.navigate(['/transfer-out']);
    } else {
      this.formReload.restart('/transfer-out/create');
    }
  }

  downloadPdf() {
    if (!this.rawBlobUrl) return;
    const link = document.createElement('a');
    link.href = this.rawBlobUrl;
    link.download = `TransferOut-${this.transferOutNumber || 'document'}.pdf`;
    link.click();
  }

  printPdf() {
    if (!this.rawBlobUrl) return;
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = this.rawBlobUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow?.print();
      // Optional: remove iframe after small delay
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
  }

  // ── UI helpers for the Zenflow kit template (header toggle, line pills,
  //    text filter, per-row state, bulk-lines dialog). No business logic here. ──
  headerOpen = true;
  lineFilter: 'all' | 'over' | 'out' = 'all';
  lineQuery = '';
  bulkOpen = false;

  /** over = quantity above stock on hand, out = nothing on hand, ok otherwise. */
  lineState(item: any): 'over' | 'out' | 'ok' {
    const soh = item?.soh;
    if (soh === 0) return 'out';
    if (Number(soh) > 0 && Number(item?.quantity || 0) > Number(soh)) return 'over';
    return 'ok';
  }

  /** Rows shown in the grid after the All / Over SOH / Out of stock pills and the text filter. */
  get visibleItems(): { item: any; index: number }[] {
    const q = (this.lineQuery || '').trim().toLowerCase();
    const out: { item: any; index: number }[] = [];
    this.transferItems.forEach((item, index) => {
      if (this.lineFilter !== 'all' && this.lineState(item) !== this.lineFilter) return;
      if (q) {
        const hay = `${item?.sku || ''} ${item?.description || ''}`.toLowerCase();
        if (!hay.includes(q)) return;
      }
      out.push({ item, index });
    });
    return out;
  }

  get overSohCount(): number {
    return this.transferItems.filter((i) => this.lineState(i) === 'over').length;
  }

  get outOfStockCount(): number {
    return this.transferItems.filter((i) => this.lineState(i) === 'out').length;
  }

  get lineCountLabel(): string {
    const shown = this.visibleItems.length;
    const all = this.transferItems.length;
    const word = all === 1 ? 'line' : 'lines';
    return shown === all ? `${all} ${word}` : `${shown} of ${all} lines`;
  }

  /** Destination name for the header summary chip. */
  get targetLocationName(): string {
    if (!this.targetLocationId) return '';
    return (
      this.filteredTargetLocations.find((l) => l.id === this.targetLocationId)?.name ||
      this.flattenLocations(this.locations).find((l) => l.id === this.targetLocationId)?.name ||
      ''
    );
  }

  /** Reason label for the header summary chip (view mode uses the stored description). */
  get selectedReasonLabel(): string {
    if (this.viewOnly) return this.selectedReasonDescription === 'N/A' ? '' : this.selectedReasonDescription;
    if (!this.selectedReasonId) return '';
    return this.reasons.find((r) => r.id === this.selectedReasonId)?.shortReason || '';
  }

  clearLineFilters(): void {
    this.lineFilter = 'all';
    this.lineQuery = '';
  }

  /** Dismiss the add-line strip without adding anything. */
  clearSelectedProduct(): void {
    this.selectedProduct = null;
    this.selectedVariant = null;
    this.selectedSearchResult = null;
    this.selectedQuantity = 1;
    this.enteredCostPrice = 0;
    this.selectedMRP = 0;
    this.allCostPrices = [];
    this.allMRPs = [];
    this.priceOptions = [];
  }

  async clearTransferLines(): Promise<void> {
    if (this.viewOnly || !this.transferItems.length) return;
    const n = this.transferItems.length;
    const proceed = await this.confirmDialog.confirm({
      title: `Clear all ${n} line${n === 1 ? '' : 's'}?`,
      message:
        'Every line on this transfer, with its quantity and price, will be removed. Search a SKU or upload the template again to start over.',
      confirmText: 'Clear lines',
      cancelText: 'Cancel',
      tone: 'neutral',
      icon: 'bi-eraser',
    });
    if (!proceed) return;
    this.transferItems = [];
    this.selectedItems.clear();
    this.clearLineFilters();
    this.updateGrandTotal();
  }

  openBulk(): void {
    if (this.viewOnly) return;
    this.bulkOpen = true;
  }

  closeBulk(): void {
    this.bulkOpen = false;
  }
}
